'use client'

import React, { createContext, useContext, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { Logomark } from '@/components/brand'
import { Icons } from '@/components/ui/icons'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useSession } from 'next-auth/react'
import { useNavBadges } from '@/components/layout/useNavBadges'
import {
  AUTO_NAME,
  isNavItemActive,
  visibleNavSections,
  type NavSection,
} from '@/lib/navigation'

// Sidebar context for collapse state
interface SidebarContextType {
  isCollapsed: boolean
  setIsCollapsed: (collapsed: boolean) => void
  toggle: () => void
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined)

export function useSidebar() {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const toggle = () => setIsCollapsed(!isCollapsed)

  return (
    <SidebarContext.Provider value={{ isCollapsed, setIsCollapsed, toggle }}>
      {children}
    </SidebarContext.Provider>
  )
}

/**
 * The sections a session sees, from the shared list in lib/navigation.ts.
 * Shared with the mobile sheet so the two never differ.
 */
export function useVisibleNavSections(): NavSection[] {
  const { data: session } = useSession()
  return visibleNavSections(session?.roles)
}

// Navigation Link Component. Text tones on the navy rail (plan 08 section 3.2):
// idle white/70 is 8.7:1 on #141A42 and section titles white/55 are 5.9:1,
// both above WCAG AA (Rule 14).
interface NavLinkProps {
  href: string
  icon: React.ElementType
  children: React.ReactNode
  isCollapsed?: boolean
  badge?: number
}

function NavLink({
  href,
  icon: Icon,
  children,
  isCollapsed,
  badge,
}: NavLinkProps) {
  const pathname = usePathname()
  const isActive = isNavItemActive(pathname, href)
  const hasBadge = badge !== undefined && badge > 0
  const badgeText = hasBadge ? (badge > 99 ? '99+' : String(badge)) : ''

  const linkContent = (
    <Link
      href={href}
      aria-current={isActive ? 'page' : undefined}
      aria-label={hasBadge && typeof children === 'string' ? `${children}, ${badgeText} waiting` : undefined}
      className={cn(
        'group relative flex items-center gap-3 rounded-xl px-3 py-2.5',
        'text-sm font-medium',
        'transition-all duration-200 ease-out',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower',
        isActive
          ? 'bg-white/[0.12] text-white shadow-soft'
          : 'text-white/70 hover:translate-x-1 hover:bg-white/10 hover:text-white',
        isCollapsed && 'justify-center px-2 hover:translate-x-0'
      )}
    >
      {/* Active marker: a cornflower bar, so the selected item reads without colour alone. */}
      {isActive && (
        <span
          aria-hidden='true'
          className='absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-brand-cornflower'
        />
      )}
      <Icon
        strokeWidth={1.5}
        aria-hidden='true'
        className={cn(
          'h-5 w-5 shrink-0 transition-colors duration-200',
          isActive ? 'text-white' : 'text-white/60 group-hover:text-white'
        )}
      />

      {isCollapsed ? (
        <span className='sr-only'>{children}</span>
      ) : (
        <span className='truncate'>{children}</span>
      )}

      {hasBadge && (
        <span
          aria-hidden='true'
          className={cn(
            'ml-auto flex h-5 min-w-5 items-center justify-center rounded-full px-1.5',
            'text-xs font-semibold text-white',
            isActive ? 'bg-white/20' : 'bg-brand-cornflower/25',
            isCollapsed && 'absolute -right-1 -top-1 h-4 min-w-4 px-1'
          )}
        >
          {badgeText}
        </span>
      )}
    </Link>
  )

  // Wrap with tooltip when collapsed
  if (isCollapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
        <TooltipContent side='right' className='ml-2'>
          {children}
        </TooltipContent>
      </Tooltip>
    )
  }

  return linkContent
}

// User section at bottom of sidebar
function SidebarUser({ isCollapsed }: { isCollapsed: boolean }) {
  const { data: session } = useSession()
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''

  if (!session?.user) return null

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-xl p-3',
        'border border-white/[0.08] bg-white/[0.04]',
        isCollapsed && 'justify-center p-2'
      )}
    >
      <Avatar
        src={session.user.image}
        fallback={session.user.name || session.user.email || '?'}
        size='sm'
        showStatus
        status='online'
      />

      {!isCollapsed && (
        <div className='min-w-0 flex-1'>
          <p className='truncate text-sm font-medium text-white'>
            {session.user.name}
          </p>
          <p className='truncate text-xs text-white/60'>
            {session.user.email}
          </p>
        </div>
      )}

      {!isCollapsed && (
        <Button
          variant='ghost'
          size='icon-sm'
          onClick={() => {
            window.location.href = `${basePath}/api/auth/logout`
          }}
          className='shrink-0 text-white/70 hover:bg-white/10 hover:text-white'
          title='Sign out'
          aria-label='Sign out'
        >
          <Icons.logout aria-hidden='true' className='h-4 w-4' />
        </Button>
      )}
    </div>
  )
}

/** Section list id, for the disclosure button's aria-controls. */
function sectionId(title: string): string {
  return `nav-section-${title.toLowerCase().replace(/\s+/g, '-')}`
}

export function Sidebar() {
  const { isCollapsed, setIsCollapsed } = useSidebar()
  // Role-aware navigation (admin / AP-Manager see the admin section,
  // AP-Operator sees the read-only pages only).
  const sections = useVisibleNavSections()
  const badges = useNavBadges()

  // Sections opened or closed by hand. A section not named here falls back to
  // its own default, so Administration and System start folded.
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})

  return (
    <TooltipProvider>
      <aside
        role='navigation'
        aria-label='Main navigation'
        className={cn(
          'hidden flex-col md:flex',
          'fixed bottom-0 left-0 top-0 z-fixed',
          // Navy rail (plan 08 WP-VIS-02): bg-primary (#141A42) with light text.
          'bg-primary text-primary-foreground',
          'border-r border-white/[0.08]',
          // Width transition
          'transition-all duration-300 ease-out',
          isCollapsed ? 'w-16' : 'w-64'
        )}
      >
        {/* Brand block: "Supervity / <AUTO_NAME> / COMMAND CENTER". */}
        <div
          className={cn(
            'flex h-20 items-center px-4',
            'border-b border-white/[0.08]',
            isCollapsed && 'justify-center px-2'
          )}
        >
          <Link
            href='/'
            className='group flex items-center gap-3 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower'
          >
            <div
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/15',
                'transition-all duration-300 ease-out',
                'group-hover:shadow-accent'
              )}
            >
              <Logomark variant='light' size={24} />
            </div>

            {isCollapsed ? (
              <span className='sr-only'>Supervity {AUTO_NAME} Command Center</span>
            ) : (
              <div className='flex flex-col transition-transform duration-200 group-hover:translate-x-0.5'>
                <span className='font-display text-lg font-bold tracking-tight text-white'>
                  Supervity
                </span>
                <span className='text-xs font-medium uppercase leading-tight tracking-widest text-white/60'>
                  {AUTO_NAME}
                  <br />
                  Command Center
                </span>
              </div>
            )}
          </Link>
        </div>

        {/* Navigation Section */}
        <nav
          aria-label='Primary navigation'
          className='scrollbar-hide flex-1 overflow-y-auto px-3 py-4'
        >
          {sections.map((section, sectionIndex) => {
            // An icons-only rail has no room for headings, so it shows everything.
            const isOpen =
              isCollapsed || (openSections[section.title] ?? !section.collapsedByDefault)
            const listId = sectionId(section.title)

            return (
              <div key={section.title} className={cn(sectionIndex > 0 && 'mt-6')}>
                {!isCollapsed &&
                  (section.collapsedByDefault ? (
                    <button
                      type='button'
                      onClick={() =>
                        setOpenSections((prev) => ({ ...prev, [section.title]: !isOpen }))
                      }
                      aria-expanded={isOpen}
                      aria-controls={listId}
                      className={cn(
                        'mb-2 flex w-full items-center gap-1.5 rounded-lg px-3 py-1',
                        'text-xs font-semibold uppercase tracking-widest text-white/55',
                        'transition-colors hover:text-white/80',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower'
                      )}
                    >
                      <span>{section.title}</span>
                      <Icons.chevronDown
                        aria-hidden='true'
                        className={cn(
                          'h-3.5 w-3.5 transition-transform duration-200',
                          !isOpen && '-rotate-90'
                        )}
                      />
                    </button>
                  ) : (
                    <p className='mb-2 px-3 text-xs font-semibold uppercase tracking-widest text-white/55'>
                      {section.title}
                    </p>
                  ))}
                {isOpen && (
                  <div id={listId} className='space-y-1'>
                    {section.items.map((item) => (
                      <NavLink
                        key={item.href}
                        href={item.href}
                        icon={item.icon}
                        isCollapsed={isCollapsed}
                        badge={item.badge ? badges[item.badge] : undefined}
                      >
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </nav>

        {/* User Section */}
        <div className='border-t border-white/[0.08] p-3'>
          <SidebarUser isCollapsed={isCollapsed} />
        </div>

        {/* Collapse Toggle */}
        <div className='border-t border-white/[0.08] p-3'>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant='ghost'
                size={isCollapsed ? 'icon-sm' : 'sm'}
                onClick={() => setIsCollapsed(!isCollapsed)}
                className={cn(
                  'w-full text-white/70 hover:bg-white/10 hover:text-white',
                  'focus-visible:ring-brand-cornflower',
                  isCollapsed && 'px-0'
                )}
                aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {isCollapsed ? (
                  <Icons.panelOpen aria-hidden='true' className='h-4 w-4' />
                ) : (
                  <>
                    <Icons.panelClose aria-hidden='true' className='h-4 w-4' />
                    <span className='ml-2'>Collapse</span>
                  </>
                )}
              </Button>
            </TooltipTrigger>
            {isCollapsed && (
              <TooltipContent side='right' className='ml-2'>
                Expand sidebar
              </TooltipContent>
            )}
          </Tooltip>
        </div>
      </aside>
    </TooltipProvider>
  )
}

